<template>
  <FloatingPopover
    ref="popover"
    v-model="opened"
    :placement="placement"
    :align="align"
    :noFlip="noFlip"
    :preferMoreSpace="overflow"
    :getBounds="visibleViewport"
    :getFitHeight="getFitHeight"
    :trackOnScroll="!IS_IOS"
    :gap="gap"
    :closeOnClickOutside="closeOnClickOutside"
    class="drop-wrapper relative flex items-center"
    :class="[
      opened ? 'open' : '',
      disabled ? 'cursor-default' : 'cursor-pointer',
      stretch ? 'w-full' : '',
    ]"
    @click="activate"
  >
    <slot name="wrapper"></slot>

    <template #content="{ availableSpace }">
      <div
        class='drop-content drop-content-root popover-panel flex
               max-sm:max-sm:max-w-[calc(100vw-0.625rem)]'
        :class="dropContentClass || ''"
        :style="overflow ? maxHeightStyle(availableSpace) : ''"
      >
        <div
          @click.stop='dropContentClick'
          class="drop-items p-2"
          :class="overflow ? 'overflow overflow-x-hidden overflow-y-auto v-tracklify-scroll' : ''"
          ref="dropItems"
        >
          <slot name="top-slot"></slot>
          <slot name="items" @close="() => close()">
            <template v-for="item in items">
              <router-link
                :key="item.key + '-route'"
                v-if="item.route"
                :to="item.route"
                :class="ITEM_CLASS"
                @click="handleClick({ item, event: $event })"
              >
                {{ $t(item.value) }}
              </router-link>
              <div v-else
                :class="ITEM_CLASS"
                :key="item.key + '-div'"
                @click="handleClick({ item, event: $event })"
              >
                <slot name="item" :item="item">
                  <slot :name="`item-${item.key}`">
                    {{ $t(item.value) }}
                    <span class="ml-2.5 text-unit text-text-muted" v-if="item.suffix">
                      {{ item.suffix }}
                    </span>
                  </slot>
                </slot>
              </div>
            </template>
          </slot>
        </div>
      </div>
    </template>
  </FloatingPopover>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, provide } from 'vue';
import FloatingPopover from '@/components/elements/FloatingPopover.vue';
import { IS_IOS, visibleViewport } from '@/composables/useFloatingPosition';

const props = defineProps({
  items: Array,
  closeOnClick: {
    type: Boolean,
    default: false,
  },
  dropContentClass: {
    type: String,
    default: '',
  },
  // the activator fills the dropdown, e.g. a tile that must fill its grid cell
  stretch: {
    type: Boolean,
    default: false,
  },
  closeOnClickOutside: {
    type: Boolean,
    default: true,
  },
  stickLeft: {
    type: Boolean,
    default: false,
  },
  stickRight: {
    type: Boolean,
    default: false,
  },
  overflow: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  openDirection: {
    type: String,
    default: '',
  },
  gap: {
    type: Number,
    default: 4,
  },
});

const emit = defineEmits(['item-click', 'open', 'visibilityChanged', 'update:opened']);

const ITEM_CLASS = `drop-item-wrapper flex h-8.5 cursor-pointer items-center rounded-chip px-2.5
  text-text transition-colors duration-100 hover:bg-hover`;

const opened = ref(false);

const dropItems = ref(null);
const popover = ref(null);

// stickLeft is the default alignment already (kept as a prop for backwards
// compatibility with existing usages); only stickRight changes it.
const align = computed(() => (props.stickRight ? 'end' : 'start'));
const placement = computed(() => (props.openDirection === 'top' ? 'top' : 'bottom'));
const noFlip = computed(() => !!props.openDirection);

function getFitHeight(contentRect) {
  if (!props.overflow) return contentRect.height;
  // scrollHeight approximates full items height regardless of current viewport clipping
  const desired = dropItems.value ? dropItems.value.scrollHeight : contentRect.height;
  return Math.min(300, desired);
}

function maxHeightStyle(availableSpace) {
  const dynamicMax = Math.max(0, Math.floor(Math.min(300, availableSpace)));
  return `max-height: ${dynamicMax}px;`;
}

const close = () => {
  if (!opened.value) return;
  opened.value = false;
};

provide('dropdownClose', close);

const OPEN_EVENT = 'dropdown:open';
const onOtherDropdownOpen = (event) => {
  if (event.detail !== close) close();
};

const handleClick = ({ item, event }) => {
  if (item.onclick) {
    item.onclick();
  }
  emit('item-click', { item, event });
};

const dropContentClick = () => {
  if (props.closeOnClick) {
    close();
  }
};

const activate = () => {
  if ((!props.closeOnClickOutside && opened.value) || props.disabled) return;

  opened.value = !opened.value;
  if (opened.value) {
    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: close }));
    emit('open');
  }
};

watch(opened, (val) => {
  emit('update:opened', val);
  emit('visibilityChanged', val);
  // only an open dropdown has to close when another one opens
  if (val) {
    document.addEventListener(OPEN_EVENT, onOtherDropdownOpen);
  } else {
    document.removeEventListener(OPEN_EVENT, onOtherDropdownOpen);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener(OPEN_EVENT, onOtherDropdownOpen);
});

// renderContent/isPositioned no longer track distinct intermediate states now that
// FloatingPopover owns mount/reveal timing internally; kept exposed (mirroring
// `opened`) only because nothing else in the codebase reads them.
defineExpose({
  close,
  activate,
  opened,
  // lets a consumer re-anchor the panel after its own action changed the page layout
  update: () => popover.value.update(),
  renderContent: opened,
  isPositioned: opened,
});
</script>


